import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { AssetDrawer } from '@/components/accountant/asset-drawer';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  assetStatus,
  lifeLabel,
  methodLabel,
  monthLabel,
  useAsset,
  type Asset,
} from '@/lib/accountant';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { nameFor } from '@/lib/arabic';
import { i18n, useLanguage, useT } from '@/lib/i18n';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { homeCurrency } from '@/lib/org-locale';
import { ltrIn } from '@/lib/sales';

export const Route = createFileRoute('/accounting/fixed-assets/$assetId')({ component: AssetPage });

const statusLabel = (s: Asset['schedule'][number]['status']): string =>
  ({
    opening: i18n.t('accountingPages.fixedAssetDetail.scheduleOpening'),
    adopted: i18n.t('accountingPages.fixedAssetDetail.scheduleAdopted'),
    posted: i18n.t('accountingPages.fixedAssetDetail.schedulePosted'),
    planned: i18n.t('accountingPages.fixedAssetDetail.schedulePlanned'),
  })[s];

/** Screen A12 (document 06): one asset with its schedule, disposal and linked entries. */
function AssetPage() {
  const t = useT();
  const { assetId } = Route.useParams();
  const orgId = useOrgId();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const asset = useAsset(assetId);
  const isStaff = me.data?.isStaff ?? false;
  const [editing, setEditing] = useState(false);
  const [disposing, setDisposing] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const reopen = useMutation({
    mutationFn: (reason: string) =>
      api<Asset>(`/orgs/${orgId}/fixed-assets/${assetId}/undo-disposal`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: ['fixed-assets', orgId] });
      setReopening(false);
      toast(t('accountingPages.fixedAssetDetail.backInRegister', { number: ltrIn(a.number) }));
    },
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/orgs/${orgId}/fixed-assets/${assetId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['fixed-assets', orgId] });
      toast(t('accountingPages.fixedAssetDetail.deleted'));
      void navigate({ to: '/accounting/fixed-assets' });
    },
  });
  if (asset.isPending)
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (asset.isError)
    return (
      <EmptyState
        title={t('accountingPages.fixedAssetDetail.loadError')}
        hint={asset.error.message}
      />
    );
  const a = asset.data;
  const booked = a.schedule.filter((r) => r.status !== 'planned').length;
  return (
    <div>
      <Link
        to="/accounting/fixed-assets"
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="rtl:-scale-x-100 size-4" />{' '}
        {t('accountingPages.fixedAssetDetail.back')}
      </Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4 rounded-lg border bg-card p-4">
        <div>
          <p className="text-sm text-muted-foreground">{a.number}</p>
          <h1 className="font-display text-[1.7rem] font-medium tracking-[-0.01em]">{a.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {a.assetAccount} · {methodLabel(a.method)}
            {a.decliningRatePct
              ? ` ${t('accountingPages.fixedAssetDetail.rateAYear', { rate: a.decliningRatePct })}`
              : ''}
            {t('accountingPages.assetDetailMore.comma')}
            {lifeLabel(a.usefulLifeMonths)} ·{' '}
            {t('accountingPages.fixedAssetDetail.inService', { date: formatDate(a.inServiceDate) })}
            {a.vendorName ? ` · ${a.vendorName}` : ''}
            {a.serialNumber
              ? ` · ${t('accountingPages.fixedAssetDetail.serial', { serial: ltrIn(a.serialNumber) })}`
              : ''}
          </p>
          <Badge className="mt-2" variant={a.status === 'disposed' ? 'outline' : 'secondary'}>
            {assetStatus(a)}
          </Badge>
        </div>
        <dl className="grid grid-cols-3 gap-6 text-end">
          <div>
            <dt className="text-xs text-muted-foreground">
              {t('accountingPages.fixedAssetDetail.cost')}
            </dt>
            <dd className="tnum text-lg font-semibold">
              {homeCurrency()} {formatAmount(a.cost)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              {t('accountingPages.fixedAssetDetail.accumulated')}
            </dt>
            <dd className="tnum text-lg font-semibold">
              {homeCurrency()} {formatAmount(a.accumulated)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              {t('accountingPages.fixedAssetDetail.nbv')}
            </dt>
            <dd className="tnum text-lg font-semibold">
              {homeCurrency()} {formatAmount(a.netBookValue)}
            </dd>
          </div>
        </dl>
        {isStaff ? (
          <div className="flex w-full justify-end gap-2">
            {a.status === 'active' ? (
              <>
                <Button variant="outline" onClick={() => setEditing(true)}>
                  {t('common.edit')}
                </Button>
                {a.adoptable.length ? (
                  <Button variant="outline" onClick={() => setAdopting(true)}>
                    {t('accountingPages.fixedAssetDetail.linkEntries', {
                      count: a.adoptable.length,
                    })}
                  </Button>
                ) : null}
                <Button onClick={() => setDisposing(true)}>
                  {t('accountingPages.fixedAssetDetail.dispose')}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setReopening(true)}>
                {t('accountingPages.fixedAssetDetail.undoDisposal')}
              </Button>
            )}
            {!booked && a.status === 'active' ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('accountingPages.fixedAssetDetail.more')}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(true)}>
                    {t('accountingPages.fixedAssetDetail.deleteAsset')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        ) : null}
      </div>

      {a.disposal ? (
        <div className="mb-4 rounded-lg border bg-card p-4 text-sm" data-testid="disposal">
          <h2 className="mb-1 text-base font-semibold">
            {t('accountingPages.fixedAssetDetail.disposedOn', {
              date: formatDate(a.disposal.date),
            })}
          </h2>
          <p>
            {a.disposal.proceedsAccount
              ? t('accountingPages.fixedAssetDetail.proceedsInto', {
                  amount: `${homeCurrency()} ${formatAmount(a.disposal.proceeds)}`,
                  account: a.disposal.proceedsAccount,
                })
              : t('accountingPages.fixedAssetDetail.proceeds', {
                  amount: `${homeCurrency()} ${formatAmount(a.disposal.proceeds)}`,
                })}{' '}
            ·{' '}
            {t(
              a.disposal.gainLoss.startsWith('-')
                ? 'accountingPages.fixedAssetDetail.lossOn'
                : 'accountingPages.fixedAssetDetail.gainOn',
              {
                amount: `${homeCurrency()} ${formatAmount(a.disposal.gainLoss.replace('-', ''))}`,
                account: a.disposal.gainLossAccount,
              },
            )}{' '}
            ·{' '}
            <Link
              to="/accounting/journal-entries/$entryId"
              params={{ entryId: a.disposal.entryId }}
              className="text-primary hover:underline"
            >
              {t('accountingPages.fixedAssetDetail.journalEntry')}
            </Link>
          </p>
          {a.disposal.reason ? <p className="text-muted-foreground">{a.disposal.reason}</p> : null}
        </div>
      ) : null}

      <h2 className="mb-2 text-base font-semibold">
        {t('accountingPages.fixedAssetDetail.scheduleTitle')}
      </h2>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('accountingPages.fixedAssetDetail.colMonth')}</TableHead>
              <TableHead className="text-end">
                {t('accountingPages.fixedAssetDetail.colDepreciation')}
              </TableHead>
              <TableHead className="text-end">
                {t('accountingPages.fixedAssetDetail.colAccumulated')}
              </TableHead>
              <TableHead className="text-end">
                {t('accountingPages.fixedAssetDetail.colNbv')}
              </TableHead>
              <TableHead>{t('accountingPages.fixedAssetDetail.colStatus')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {a.schedule.map((r) => (
              <TableRow
                key={`${r.status}-${r.month}`}
                className={r.status === 'planned' ? 'text-muted-foreground' : ''}
              >
                <TableCell>
                  {r.status === 'opening'
                    ? t('accountingPages.fixedAssetDetail.throughMonth', {
                        month: monthLabel(r.month),
                      })
                    : monthLabel(r.month)}
                </TableCell>
                <TableCell className="tnum text-end">
                  {r.entryId ? (
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: r.entryId }}
                      className="text-primary hover:underline"
                    >
                      {formatAmount(r.charge)}
                    </Link>
                  ) : (
                    formatAmount(r.charge)
                  )}
                </TableCell>
                <TableCell className="tnum text-end">{formatAmount(r.accumulated)}</TableCell>
                <TableCell className="tnum text-end">{formatAmount(r.netBookValue)}</TableCell>
                <TableCell>{statusLabel(r.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AssetDrawer open={editing} asset={a} onOpenChange={setEditing} />
      <DisposeDialog open={disposing} asset={a} onClose={() => setDisposing(false)} />
      <AdoptDialog open={adopting} asset={a} onClose={() => setAdopting(false)} />
      <ReasonDialog
        open={reopening}
        title={t('accountingPages.fixedAssetDetail.undoDisposalTitle', { number: ltrIn(a.number) })}
        description={t('accountingPages.fixedAssetDetail.undoDisposalDescription')}
        confirmLabel={t('accountingPages.fixedAssetDetail.undoDisposal')}
        destructive
        pending={reopen.isPending}
        error={reopen.error?.message ?? null}
        onConfirm={(reason) => reopen.mutate(reason)}
        onClose={() => {
          reopen.reset();
          setReopening(false);
        }}
      />
      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('accountingPages.fixedAssetDetail.deleteTitle', { number: ltrIn(a.number) })}
            </DialogTitle>
            <DialogDescription>
              {t('accountingPages.fixedAssetDetail.deleteDescription')}
            </DialogDescription>
          </DialogHeader>
          <ErrorBanner>{remove.error?.message ?? null}</ErrorBanner>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t('accountingPages.fixedAssetDetail.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DisposeDialog({
  open,
  asset,
  onClose,
}: {
  open: boolean;
  asset: Asset;
  onClose: () => void;
}) {
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const accounts = useAccounts();
  const [date, setDate] = useState(todayIso());
  const [proceeds, setProceeds] = useState('');
  const [proceedsAccountId, setProceedsAccountId] = useState('');
  const [gainLossAccountId, setGainLossAccountId] = useState('');
  const [reason, setReason] = useState('');
  const list = accounts.data ?? [];
  const proceedsAccounts = list.filter((a) => a.isActive && a.class === 'asset' && !a.isControl);
  const glAccounts = list.filter(
    (a) => a.isActive && (a.class === 'income' || a.class === 'expense') && !a.isControl,
  );
  useEffect(() => {
    if (!open) return;
    setDate(todayIso());
    setProceeds('');
    setReason('');
    setProceedsAccountId(proceedsAccounts.find((a) => a.accountType === 'bank')?.id ?? '');
    setGainLossAccountId(
      (
        glAccounts.find((a) => a.detailType === 'other_income') ??
        glAccounts.find((a) => a.class === 'income')
      )?.id ?? '',
    );
    // the defaults depend on the chart only when the dialog opens
  }, [open, list.length]);
  const dateError = !date
    ? t('accountingPages.fixedAssetDetail.disposalDateRequired')
    : date < asset.acquisitionDate
      ? t('accountingPages.fixedAssetDetail.disposalBeforeAcquired')
      : undefined;
  const cancel = () => {
    dispose.reset();
    onClose();
  };
  const dispose = useMutation({
    mutationFn: () =>
      api<Asset>(`/orgs/${orgId}/fixed-assets/${asset.id}/dispose`, {
        method: 'POST',
        body: JSON.stringify({
          date,
          proceeds: proceeds || '0',
          ...(proceeds && proceedsAccountId ? { proceedsAccountId } : {}),
          gainLossAccountId,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        }),
      }),
    onSuccess: async (a) => {
      await qc.invalidateQueries();
      toast(t('accountingPages.assetDetailMore.disposedToast', { number: ltrIn(a.number) }), {
        link: {
          to: `/accounting/journal-entries/${a.disposal!.entryId}`,
          label: t('accountingPages.fixedAssetDetail.viewEntry'),
        },
      });
      onClose();
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          dispose.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('accountingPages.fixedAssetDetail.disposeTitle', {
              number: ltrIn(asset.number),
              name: asset.name,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('accountingPages.fixedAssetDetail.disposeDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <ErrorBanner>{dispose.error?.message ?? null}</ErrorBanner>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('accountingPages.fixedAssetDetail.disposalDate')} error={dateError}>
              {(id) => <DateInput id={id} value={date} onChange={setDate} />}
            </Field>
            <Field
              label={t('accountingPages.fixedAssetDetail.proceedsLabel', {
                currency: homeCurrency(),
              })}
              hint={t('accountingPages.fixedAssetDetail.proceedsHint')}
            >
              {(id) => (
                <AmountInput
                  id={id}
                  className="h-9 w-full rounded-md border border-input bg-card px-3 text-end"
                  value={proceeds}
                  onValueChange={setProceeds}
                />
              )}
            </Field>
          </div>
          {proceeds ? (
            <Field label={t('accountingPages.fixedAssetDetail.proceedsPaidInto')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={proceedsAccountId}
                  onChange={(e) => setProceedsAccountId(e.target.value)}
                >
                  <option value="">{t('accountingPages.fixedAssetDetail.chooseAccount')}</option>
                  {proceedsAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} {nameFor(lang, a.fullName, a.fullNameAr)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          <Field label={t('accountingPages.fixedAssetDetail.gainLossAccount')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={gainLossAccountId}
                onChange={(e) => setGainLossAccountId(e.target.value)}
              >
                <option value="">{t('accountingPages.fixedAssetDetail.chooseAccount')}</option>
                {glAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {nameFor(lang, a.fullName, a.fullNameAr)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('accountingPages.fixedAssetDetail.note')}>
            {(id) => (
              <Textarea
                id={id}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={cancel}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={
              dispose.isPending ||
              Boolean(dateError) ||
              !gainLossAccountId ||
              (Boolean(proceeds) && !proceedsAccountId)
            }
            onClick={() => dispose.mutate()}
          >
            {dispose.isPending
              ? t('accountingPages.fixedAssetDetail.posting')
              : t('accountingPages.fixedAssetDetail.dispose')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdoptDialog({
  open,
  asset,
  onClose,
}: {
  open: boolean;
  asset: Asset;
  onClose: () => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [ids, setIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setIds(new Set());
  }, [open]);
  const total = useMemo(
    () =>
      asset.adoptable
        .filter((e) => ids.has(e.entryId))
        .reduce((s, e) => s + (toMinor(e.amount) ?? 0n), 0n),
    [asset.adoptable, ids],
  );
  const adopt = useMutation({
    mutationFn: () =>
      api<Asset>(`/orgs/${orgId}/fixed-assets/${asset.id}/adopt`, {
        method: 'POST',
        body: JSON.stringify({ entryIds: [...ids] }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['fixed-assets', orgId] });
      toast(
        t('accountingPages.fixedAssetDetail.linkedToast', {
          count: ids.size,
          number: ltrIn(asset.number),
        }),
      );
      onClose();
    },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>{t('accountingPages.fixedAssetDetail.adoptTitle')}</DialogTitle>
          <DialogDescription>
            {t('accountingPages.fixedAssetDetail.adoptDescription', {
              account: asset.accumulatedAccount,
            })}
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner>{adopt.error?.message ?? null}</ErrorBanner>
        <div className="max-h-80 overflow-y-auto rounded-md border">
          {asset.adoptable.map((e) => (
            <div
              key={e.entryId}
              className="flex items-center gap-3 border-b px-3 py-2 text-sm last:border-0"
            >
              <Checkbox
                label={t('accountingPages.fixedAssetDetail.entryCheckbox', {
                  date: formatDate(e.date),
                  number: e.entryNo,
                })}
                checked={ids.has(e.entryId)}
                onChange={() =>
                  setIds((s) => {
                    const n = new Set(s);
                    if (n.has(e.entryId)) n.delete(e.entryId);
                    else n.add(e.entryId);
                    return n;
                  })
                }
              />
              <span className="flex-1 truncate text-muted-foreground">{e.memo}</span>
              <span className="tnum">{formatAmount(e.amount)}</span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <span className="tnum me-auto self-center text-sm text-muted-foreground">
            {ids.size
              ? t('accountingPages.fixedAssetDetail.selected', {
                  amount: `${homeCurrency()} ${formatMinor(total)}`,
                })
              : ''}
          </span>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!ids.size || adopt.isPending} onClick={() => adopt.mutate()}>
            {t('accountingPages.fixedAssetDetail.linkEntriesButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
